import { useEffect, useState, useCallback } from "react";
import { CheckCircle2, XCircle, Image as ImageIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, apiError } from "@/lib/api";
import { rupee, statusColor } from "@/lib/format";
import ListToolbar from "@/components/ListToolbar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TYPE = { subscription: "Subscription", slot: "Additional Slots", renewal: "Renewal" };
const STATUS = { pending: "Pending", screenshot_uploaded: "Under Review", approved: "Approved", rejected: "Rejected" };

export default function Payments() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ type: "", seller_role: "", status: "" });
  const [sort, setSort] = useState("newest");
  const [shot, setShot] = useState(null);

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (search) p.set("q", search);
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    p.set("sort", sort);
    const { data } = await api.get(`/admin/payments?${p.toString()}`);
    setItems(data);
  }, [search, filters, sort]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const act = async (fn) => { try { await fn(); toast.success("Done"); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-extrabold">Payments</h1>
        <p className="text-muted-foreground text-sm font-medium">Review payment screenshots and approve to activate subscriptions.</p>
      </div>

      <ListToolbar
        placeholder="Search invoice number…"
        search={search} setSearch={setSearch}
        filters={[
          { key: "type", label: "Type", value: filters.type, options: Object.entries(TYPE).map(([v, l]) => ({ value: v, label: l })) },
          { key: "seller_role", label: "Seller", value: filters.seller_role, options: [{ value: "manufacturer", label: "Manufacturer" }, { value: "wholesaler", label: "Wholesaler" }, { value: "retailer", label: "Retailer" }] },
          { key: "status", label: "Status", value: filters.status, options: Object.entries(STATUS).map(([v, l]) => ({ value: v, label: l })) },
        ]}
        onFilter={(k, v) => setFilters((f) => ({ ...f, [k]: v }))}
        sort={sort} setSort={setSort}
        sorts={[{ value: "newest", label: "Newest" }, { value: "oldest", label: "Oldest" }, { value: "highest", label: "Highest Amount" }, { value: "lowest", label: "Lowest Amount" }]}
        onReset={() => { setSearch(""); setFilters({ type: "", seller_role: "", status: "" }); setSort("newest"); }}
      />

      <div className="bg-card border rounded-xl overflow-x-auto ct-scroll">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Invoice</TableHead><TableHead>Company</TableHead><TableHead>Type</TableHead>
            <TableHead>Amount</TableHead><TableHead>Screenshot</TableHead><TableHead>Status</TableHead><TableHead>Action</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                <TableCell className="font-mono text-xs">{p.invoice_no}</TableCell>
                <TableCell className="font-semibold">{p.company_name}<div className="text-xs text-muted-foreground capitalize">{p.seller_role}</div></TableCell>
                <TableCell>{TYPE[p.type]}</TableCell>
                <TableCell className="font-bold">{rupee(p.amount)}</TableCell>
                <TableCell>{p.screenshot_url ? <Button size="sm" variant="ghost" onClick={() => setShot(p.screenshot_url)} data-testid={`view-shot-${p.id}`}><ImageIcon className="h-4 w-4" /></Button> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                <TableCell><Badge className={`border ${statusColor(p.status)}`}>{STATUS[p.status]}</Badge></TableCell>
                <TableCell>
                  <div className="flex gap-1 flex-wrap">
                    {p.screenshot_url && (
                      <Button size="sm" variant="outline" data-testid={`view-payment-shot-${p.id}`} onClick={() => setShot(p.screenshot_url)} className="gap-1"><ImageIcon className="h-4 w-4" /> View Payment Screenshot</Button>
                    )}
                    {p.status === "screenshot_uploaded" && (
                      <>
                        <Button size="sm" data-testid={`approve-pay-${p.id}`} onClick={() => act(() => api.patch(`/admin/payments/${p.id}/approve`))} className="bg-emerald-600 hover:bg-emerald-700 gap-1"><CheckCircle2 className="h-4 w-4" /></Button>
                        <Button size="sm" variant="outline" data-testid={`reject-pay-${p.id}`} onClick={() => act(() => api.patch(`/admin/payments/${p.id}/reject`))}><XCircle className="h-4 w-4 text-rose-600" /></Button>
                      </>
                    )}
                    <Button size="sm" variant="outline" data-testid={`delete-pay-${p.id}`} onClick={() => { if (window.confirm("Delete this payment record?")) act(() => api.delete(`/admin/payments/${p.id}`)); }}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground font-semibold">No payments.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!shot} onOpenChange={(v) => !v && setShot(null)}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Payment Screenshot</DialogTitle></DialogHeader>
          {shot && <img src={fileUrl(shot)} alt="screenshot" className="w-full rounded-lg border" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
